import { horizontalListSortingStrategy, SortableContext, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  type Cell,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type Header,
  type HeaderGroup,
  useReactTable,
  type Table as TableType,
  type RowData,
} from "@tanstack/react-table";
import { AlertCircle, ChevronDown, ChevronsUpDown, ChevronUp, GripVertical, SlidersHorizontal } from "lucide-react";
import { memo, useMemo, type CSSProperties } from "react";
import { usePrevious } from "react-use";
import { useSpinDelay } from "spin-delay";
import { EmptyState } from "../EmptyState";
import { Skeleton } from "../ui/skeleton";
import { type useTableColumnOrder } from "./useTableColumnOrder";
import { ColumnVisibilityToggle } from "@/components/ColumnVisibilityToggle";
import { type TableProps } from "@/components/DataTable";
import { useTableColumnVisibility } from "@/components/DataTable/useTableColumnVisibility";
import { Pagination } from "@/components/Pagination";
import { Tooltip } from "@/components/Tooltip";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils/common";

const CELL_HEIGHT = 40 as const;

type Props<TData extends RowData> = Pick<
  ReturnType<typeof useTableColumnOrder>,
  "columnOrder" | "onColumnOrderChange"
> &
  TableProps<TData>;

export function TableImplementation<TData>({
  columns,
  data,
  pagination,
  tableId,
  columnOrder,
  onColumnOrderChange,
  sortConfig,
  loading,
  errorMessage,
  onRowClick,
  filterButton,
  searchFilter,
}: Props<TData>) {
  const { columnVisibility, onColumnVisibilityChange } = useTableColumnVisibility(tableId);

  const prevData = usePrevious(data);
  const displayData = loading && prevData ? prevData : data;

  const isClientSide = pagination.type === "client";

  const table = useReactTable<TData>({
    data: displayData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    ...(isClientSide && {
      getPaginationRowModel: getPaginationRowModel(),
      getSortedRowModel: getSortedRowModel(),
    }),
    manualPagination: !isClientSide,
    manualSorting: !isClientSide,
    sortDescFirst: true,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    defaultColumn: {
      enableResizing: true,
      enableSorting: false,
    },
    onColumnOrderChange,
    onColumnVisibilityChange,
    onSortingChange: sortConfig?.onSortingChange,
    ...(isClientSide && {
      onPaginationChange: pagination.setPagination,
    }),
    state: {
      columnVisibility,
      columnOrder,
      sorting: sortConfig?.sorting,
      ...(isClientSide && {
        pagination: pagination.paginationState,
      }),
    },
  });

  const { getFlatHeaders } = table;
  const columnSizingInfo = table.getState().columnSizingInfo;
  const columnSizing = table.getState().columnSizing;

  const columnSizeVars = useMemo(() => {
    if (columnSizingInfo && columnSizing) {
      const headers = getFlatHeaders();
      const colSizes: { [key: string]: number } = {};
      for (let i = 0; i < headers.length; i++) {
        const header = headers[i]!;
        colSizes[`--header-${header.id}-size`] = header.getSize();
        colSizes[`--col-${header.column.id}-size`] = header.column.getSize();
      }
      return colSizes;
    }
  }, [columnSizing, columnSizingInfo, getFlatHeaders]);

  if (errorMessage) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center border-t border-b border-solid border-gray-300 my-4 bg-red-50">
        <ErrorState title="An error occurred" message={errorMessage} />
      </div>
    );
  }

  return (
    <section>
      <div className={cn("flex p-2 px-4 gap-2 items-center", searchFilter ? "justify-between" : "justify-end")}>
        {searchFilter ? <div className="flex-1 min-w-0">{searchFilter}</div> : null}
        <div className="flex gap-2 items-center">
          <ColumnVisibilityToggle table={table} disabled={loading} />
          {filterButton ? (
            <Button variant="outline" onClick={filterButton.onClick}>
              <SlidersHorizontal className="mr-2 h-4 w-4" />
              {filterButton.label || "Filters"} {filterButton.activeCount ? `(${filterButton.activeCount})` : ""}
            </Button>
          ) : null}
        </div>
      </div>
      {!loading && (!data || data.length === 0) ? (
        <div className="flex min-h-[400px] flex-col items-center justify-center border-t border-b border-solid border-gray-300 my-4 bg-gray-50">
          <EmptyState
            title="No data available"
            description="Try adjusting your search or filters."
            animationPath="/animations/searching.json"
            size={250}
          />
        </div>
      ) : (
        <>
          <div className="max-h-[600px] overflow-auto w-full">
            <Table
              style={columnSizeVars}
              className="[&_tr_th:first-child]:border-l [&_tr_td:first-child]:border-l border-separate border-spacing-0 w-max min-w-full"
            >
              <TableHeader className="sticky top-0 bg-table-header z-10">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableHeaderContent
                    key={headerGroup.id}
                    headerGroup={headerGroup}
                    columnOrder={columnOrder}
                    loading={loading}
                  />
                ))}
              </TableHeader>
              {table.getState().columnSizingInfo.isResizingColumn ? (
                <MemoizedTableBody
                  columnOrder={columnOrder}
                  table={table as TableType<any>}
                  pageSize={Number(pagination.paginationInfo.pageSize)}
                  loading={loading}
                />
              ) : (
                <TableBodyContent
                  columnOrder={columnOrder}
                  table={table}
                  pageSize={Number(pagination.paginationInfo.pageSize)}
                  loading={loading}
                  onRowClick={onRowClick}
                />
              )}
            </Table>
          </div>
          <div className="flex justify-end p-2 px-4">
            <Pagination pagination={pagination} />
          </div>
        </>
      )}
    </section>
  );
}

const SKELETON_SIZES = {
  row: `h-[${CELL_HEIGHT}px]`,
  cell: { height: "h-5", width: "w-[80%]" },
} as const;

function TableRowSkeleton({ columns }: { columns: number }) {
  return (
    <TableRow className={SKELETON_SIZES.row} data-testid="table-row-skeleton">
      {Array.from({ length: columns }).map((_, i) => (
        <TableCell className="border-r border-b" key={i}>
          <Skeleton className={cn(SKELETON_SIZES.cell.height, SKELETON_SIZES.cell.width)} />
        </TableCell>
      ))}
    </TableRow>
  );
}

function TableBodyLoading({ pageSize, columns }: { pageSize: number; columns: number }) {
  return (
    <TableBody data-testid="table-loading-body">
      {Array.from({ length: pageSize }).map((_, index) => (
        <TableRowSkeleton key={index} columns={columns} />
      ))}
    </TableBody>
  );
}

const TableBodyContent = <TData extends RowData>({
  table,
  columnOrder,
  pageSize,
  loading,
  onRowClick,
}: {
  table: TableType<TData>;
  columnOrder: Array<string>;
  pageSize: number;
  loading: boolean;
  onRowClick?: (row: TData) => void;
}) => {
  const showSkeletonLoader = useSpinDelay(loading, {
    delay: 300,
    minDuration: 350,
  });

  const isInitialLoad = loading && !table.getRowModel().rows.length;

  if (showSkeletonLoader || isInitialLoad) {
    return <TableBodyLoading pageSize={pageSize} columns={table.getVisibleFlatColumns().length} />;
  }

  const rows = table.getRowModel().rows;

  return (
    <TableBody className="[&_tr:last-child]:border-1">
      {rows.map((row) => (
        <TableRow
          key={row.id}
          data-state={row.getIsSelected() && "selected"}
          className={onRowClick ? "cursor-pointer" : "cursor-auto"}
          onClick={onRowClick ? () => onRowClick(row.original) : undefined}
        >
          {row.getVisibleCells().map((cell) => (
            <SortableContext key={cell.id} items={columnOrder} strategy={horizontalListSortingStrategy}>
              <DragAlongCell key={cell.id} cell={cell} loading={loading} />
            </SortableContext>
          ))}
        </TableRow>
      ))}
    </TableBody>
  );
};

const MemoizedTableBody = memo(
  TableBodyContent,
  <TData extends RowData>(
    prev: { table: TableType<TData>; loading: boolean; errorMessage?: string },
    next: { table: TableType<TData>; loading: boolean; errorMessage?: string },
  ) => {
    return (
      prev.table.options.data === next.table.options.data &&
      prev.loading === next.loading &&
      prev.errorMessage === next.errorMessage
    );
  },
);

function TableHeaderContent<TData>({
  headerGroup,
  columnOrder,
  loading,
}: {
  headerGroup: HeaderGroup<TData>;
  columnOrder: string[];
  loading: boolean;
}) {
  return (
    <TableRow key={headerGroup.id} className={cn(loading && "opacity-60 pointer-events-none")}>
      <SortableContext items={columnOrder} strategy={horizontalListSortingStrategy}>
        {headerGroup.headers.map((header) => (
          <DraggableTableHeader key={header.id} header={header} disabled={loading} />
        ))}
      </SortableContext>
    </TableRow>
  );
}

const DraggableTableHeader = <TData extends RowData>({
  header,
  disabled,
}: {
  header: Header<TData, any>;
  disabled: boolean;
}) => {
  const hasHeader = !header.isPlaceholder && header.column.columnDef.header;
  const isDraggable = hasHeader && !disabled;

  const { attributes, isDragging, listeners, setNodeRef, transform } = useSortable({
    id: header.column.id,
    disabled: !isDraggable,
  });

  const style: CSSProperties = {
    opacity: isDragging ? 0.8 : 1,
    transform: CSS.Translate.toString(transform),
    whiteSpace: "nowrap",
    width: `calc(var(--header-${header.id}-size) * 1px)`,
    zIndex: isDragging ? 1 : 0,
  };

  return (
    <TableHead
      colSpan={header.colSpan}
      ref={setNodeRef}
      style={style}
      className={cn(
        "group relative overflow-hidden",
        "border-r border-t border-b text-foreground",
        "[&.resizing]:select-none",
        header.column.getIsResizing() && "resizing",
        disabled && "cursor-default",
      )}
    >
      {isDraggable ? (
        <Tooltip asChild label="Drag to reorder">
          <button
            className="absolute left-1 top-3 w-4 opacity-0 group-hover:opacity-100"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4 hover:cursor-grab" />
          </button>
        </Tooltip>
      ) : null}
      <div
        onClick={disabled ? undefined : header.column.getToggleSortingHandler()}
        className={cn(
          "transition-transform duration-200",
          // Default state: All headers at x-0, with hover effect at x-3 when not loading
          disabled ? "translate-x-0" : ["translate-x-0", "group-hover:translate-x-3"],
          // Enable pointer cursor for sortable columns when not loading
          !disabled && header.column.getCanSort() && "cursor-pointer",
          // Keep sorted column pushed right (x-3) during loading to prevent visual jumping
          disabled && header.column.getIsSorted() && "translate-x-3",
          "line-clamp-1 text-wrap pr-6",
        )}
      >
        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
      </div>
      {header.column.getCanResize() && (
        <div
          onMouseDown={disabled ? undefined : header.getResizeHandler()}
          onTouchStart={disabled ? undefined : header.getResizeHandler()}
          className={cn(
            "absolute right-0 top-0 z-10 h-full w-1",
            !disabled && "cursor-col-resize group-hover:bg-border",
            header.column.getIsResizing() && "bg-muted-foreground",
          )}
        />
      )}
      {header.column.getCanSort() && (
        <span className="absolute right-2 top-3 pointer-events-none">
          {!header.column.getIsSorted() ? (
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          ) : (
            {
              asc: <ChevronDown className="h-4 w-4" />,
              desc: <ChevronUp className="h-4 w-4" />,
            }[header.column.getIsSorted() as string]
          )}
        </span>
      )}
    </TableHead>
  );
};

const DragAlongCell = <TData,>({ cell, loading }: { cell: Cell<TData, any>; loading: boolean }) => {
  const { isDragging, setNodeRef, transform } = useSortable({
    id: cell.column.id,
  });

  const style: CSSProperties = {
    opacity: loading ? 0.6 : isDragging ? 0.8 : 1,
    position: "relative",
    transform: CSS.Translate.toString(transform), // translate instead of transform to avoid squishing
    width: `calc(var(--col-${cell.column.id}-size) * 1px)`,
    zIndex: isDragging ? 1 : 0,
  };

  return (
    <TableCell
      style={style}
      className={cn(
        "border-r border-b max-w-[1px]",
        "overflow-hidden text-ellipsis whitespace-nowrap",
        `h-[${CELL_HEIGHT}px]`,
      )}
      ref={setNodeRef}
    >
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </TableCell>
  );
};

interface ErrorStateProps {
  title?: string;
  message: string;
  className?: string;
}

function ErrorState({ title = "Error", message, className = "" }: ErrorStateProps) {
  return (
    <div data-testid="error-state" className={`flex flex-col items-center justify-center p-4 ${className}`}>
      <div className="flex items-center justify-center gap-2 text-red-500 mb-2">
        <AlertCircle className="h-6 w-6" />
        <h3 className="text-lg font-semibold">{title}</h3>
      </div>
      <p className="text-sm text-red-600">{message}</p>
    </div>
  );
}
